import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { parseDPCRExcel } from '@/utils/excelParser';
import { useToast } from '@/components/ui/use-toast';
import { createClient } from '@supabase/supabase-js';
import { FileUp } from 'lucide-react';

const supabase = createClient(
  "https://gxjhhfllwtodskynhorr.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4amhoZmxsd3RvZHNreW5ob3JyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE0OTg2NjAsImV4cCI6MjA1NzA3NDY2MH0.mg_8gXMXwEgZ0Cng7TVzm9q3-7xM6eQzCkTiF4CTaGc"
);

export function ImportMFOModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (!file.name.endsWith('.xlsx')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an Excel (.xlsx) file',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoading(true);

      // Parse the Excel file using the worker-based parser.
      const mfoData = await parseDPCRExcel(file);
      // mfoData has the structure: [{ mfo: string, successIndicators: string[] }, ... ]

      // Map the parsed data to the structure we need for the mfo_items table.
      const mfoItems = mfoData.map((mfo: any, index: number) => {
        // Try to extract a code (e.g., "MFO 1.1A") from the mfo text.
        const codeMatch = mfo.mfo.match(/^(MFO\s*\S+)/i);
        const code = codeMatch ? codeMatch[1] : `MFO-${index + 1}`;
        return {
          code,
          title: mfo.mfo,
          status: 'not-started',
          priority: 'low',
          progress: 0,
          q1: false,
          q2: false,
          q3: false,
          q4: false,
          due_date: new Date().toISOString(), // Adjust this if you later extract a due date.
          department_id: "526c0dd6-999e-40ee-a734-7fb73afeaa33"
        };
      });

      // Insert the MFO items into the database.
      const { data, error } = await supabase
        .from('mfo_items')
        .insert(mfoItems)
        .select();

      if (error) throw error;

      // Create success indicator entries by mapping each indicator to its corresponding MFO.
      const indicatorsToInsert = mfoData.flatMap((mfo: any, index: number) => 
        mfo.successIndicators.map((indicator: string) => ({
          indicator,
          mfo_id: data[index]?.id, // Assumes the order of inserted items matches mfoData order.
        }))
      );

      const { error: indicatorError } = await supabase
        .from("mfo_success_indicators")
        .insert(indicatorsToInsert);

      if (indicatorError) throw indicatorError;

      // Create kanban cards for each MFO.
      const { error: kanbanError } = await supabase
        .from("kanban_cards")
        .insert(
          data.map((mfo: any) => ({
            title: mfo.title,
            description: mfo.title,
            priority: mfo.priority,
            progress: mfo.progress,
            due_date: mfo.due_date,
            mfo_id: mfo.id,
            column_id: "dd19069e-20ce-4aca-af23-ce54f874c4f4", // default: not started
            department_id: "526c0dd6-999e-40ee-a734-7fb73afeaa33",
          }))
        );
        
      if (kanbanError) throw kanbanError;

      toast({
        title: 'Import successful',
        description: `${mfoData.length} MFOs have been imported successfully.`,
      });
      setIsOpen(false);

      // Add a notification.
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          title: 'New MFOs imported',
          message: `${mfoData.length} MFOs have been imported successfully.`,
          type: 'success',
        });

      if (notificationError) throw notificationError;

      // Refresh the page to update the data.
      window.location.reload();
    } catch (error) {
      const errMessage = typeof error === 'string'
        ? error
        : (error as any)?.message || 'Unknown error';
      console.error('Error importing MFOs:', error);
      toast({
        title: 'Import failed',
        description: errMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FileUp className="mr-2 h-4 w-4" />
          Import DPCR
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Import MFOs from Excel</DialogTitle>
        </DialogHeader>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'}`}
        >
          <input {...getInputProps()} />
          {isLoading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p>Importing MFOs...</p>
            </div>
          ) : isDragActive ? (
            <p>Drop the Excel file here...</p>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <p>Drag and drop an Excel file here, or click to select</p>
              <p className="text-sm text-muted-foreground">
                Only .xlsx files are supported
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
